import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@nt/ui/table";
import { buttonVariants } from "@nt/ui/recipes";
import { useState } from "react";

import { Section } from "@/components/section";
import { Segmented } from "@/components/segmented";
import { money } from "@/lib/format";
import type { PerformanceData } from "@/lib/types";
import {
  bucketTitle,
  count,
  ordersPer100Clicks,
} from "@/pages/performance/format";

export type Grouping = "day" | "month";

type Bucket = PerformanceData["series"][number];

const GROUPS = [
  { value: "day", label: "Day" },
  { value: "month", label: "Month" },
] as const;

/**
 * The series as table rows, newest first. `month` adds the days of each
 * `YYYY-MM` together; a series that is already monthly stays as it is.
 */
export function seriesRows(series: Bucket[], group: Grouping): Bucket[] {
  if (group === "day") return [...series].reverse();
  const months = new Map<string, Bucket>();
  for (const b of series) {
    const key = b.date.slice(0, 7);
    const m = months.get(key) ?? {
      date: key,
      clicks: 0,
      signups: 0,
      firstOrders: 0,
      earnedCents: 0,
    };
    m.clicks += b.clicks;
    m.signups += b.signups;
    m.firstOrders += b.firstOrders;
    m.earnedCents += b.earnedCents;
    months.set(key, m);
  }
  return [...months.values()].sort((a, b) => b.date.localeCompare(a.date));
}

function sum(rows: Bucket[]): Omit<Bucket, "date"> {
  return rows.reduce(
    (t, b) => ({
      clicks: t.clicks + b.clicks,
      signups: t.signups + b.signups,
      firstOrders: t.firstOrders + b.firstOrders,
      earnedCents: t.earnedCents + b.earnedCents,
    }),
    { clicks: 0, signups: 0, firstOrders: 0, earnedCents: 0 },
  );
}

const num = "text-right tabular-nums";

/** A bucket with nothing in it: no click, sign-up, order or fee. */
export const isEmptyBucket = (b: Bucket) =>
  b.clicks === 0 &&
  b.signups === 0 &&
  b.firstOrders === 0 &&
  b.earnedCents === 0;

/** "Detailed breakdown": the range day by day (or month by month) with a totals row. */
export function SeriesTable({
  data,
  defaultGroup = "day",
}: {
  data: PerformanceData;
  defaultGroup?: Grouping;
}) {
  const monthly = data.days === 365;
  const [group, setGroup] = useState<Grouping>(defaultGroup);
  const [showEmpty, setShowEmpty] = useState(false);
  const all = seriesRows(data.series, monthly ? "month" : group);
  const total = sum(all);
  const emptyCount = all.filter(isEmptyBucket).length;
  const rows = showEmpty ? all : all.filter((b) => !isEmptyBucket(b));
  const unit = monthly || group === "month" ? "month" : "day";
  return (
    <Section
      title="Detailed breakdown"
      description={
        monthly || group === "month"
          ? "Months with activity, newest first."
          : "Days with activity, newest first."
      }
      actions={
        monthly ? null : (
          <Segmented
            aria-label="Group by"
            size="sm"
            value={group}
            onChange={setGroup}
            options={GROUPS}
          />
        )
      }
      contentClassName="px-0"
    >
      <div className="hidden sm:block">
        <Table density="compact">
          <TableHeader>
            <TableRow>
              <TableHead>{unit === "month" ? "Month" : "Day"}</TableHead>
              <TableHead className={num}>Clicks</TableHead>
              <TableHead className={num}>Sign-ups</TableHead>
              <TableHead className={num}>First orders</TableHead>
              <TableHead className={num}>Orders per 100 clicks</TableHead>
              <TableHead className={num}>Fees earned</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((b) => (
              <TableRow key={b.date}>
                <TableCell>{bucketTitle(b.date)}</TableCell>
                <TableCell className={num}>{count(b.clicks)}</TableCell>
                <TableCell className={num}>{count(b.signups)}</TableCell>
                <TableCell className={num}>{count(b.firstOrders)}</TableCell>
                <TableCell className={num}>
                  {ordersPer100Clicks(b.firstOrders, b.clicks)}
                </TableCell>
                <TableCell className={num}>{money(b.earnedCents)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-medium">Total</TableCell>
              <TableCell className={num}>{count(total.clicks)}</TableCell>
              <TableCell className={num}>{count(total.signups)}</TableCell>
              <TableCell className={num}>{count(total.firstOrders)}</TableCell>
              <TableCell className={num}>
                {ordersPer100Clicks(total.firstOrders, total.clicks)}
              </TableCell>
              <TableCell className={num}>{money(total.earnedCents)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <div className="sm:hidden">
        <Table density="compact">
          <TableHeader>
            <TableRow>
              <TableHead>{unit === "month" ? "Month" : "Day"}</TableHead>
              <TableHead className={num}>Clicks</TableHead>
              <TableHead className={num}>Sign-ups</TableHead>
              <TableHead className={num}>Fees</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((b) => (
              <TableRow key={b.date}>
                <TableCell className="whitespace-nowrap">
                  {bucketTitle(b.date)}
                </TableCell>
                <TableCell className={num}>{count(b.clicks)}</TableCell>
                <TableCell className={num}>{count(b.signups)}</TableCell>
                <TableCell className={num}>{money(b.earnedCents)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-medium">Total</TableCell>
              <TableCell className={num}>{count(total.clicks)}</TableCell>
              <TableCell className={num}>{count(total.signups)}</TableCell>
              <TableCell className={num}>{money(total.earnedCents)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      {emptyCount > 0 ? (
        <div className="px-(--card-spacing) pt-3">
          <button
            type="button"
            className={buttonVariants({ variant: "ghost", size: "sm" })}
            onClick={() => setShowEmpty((v) => !v)}
          >
            {showEmpty
              ? `Hide ${unit}s with no activity`
              : `Show ${emptyCount} ${unit}${emptyCount === 1 ? "" : "s"} with no activity`}
          </button>
        </div>
      ) : null}
    </Section>
  );
}
